import { FC, createContext, ReactNode, useState } from "react";
import moment from "moment";

import { DatePickerState, CalendarLayout } from "./datePicker.type";

interface DatePickerStoreState extends DatePickerState {
  incrementSelectedYearsRange: () => void;
  decrementSelectedYearsRange: () => void;
  incrementSelectedYear: () => void;
  decrementSelectedYear: () => void;
  incrementSelectedMonth: () => void;
  decrementSelectedMonth: () => void;
  setSelectedYear: (year: number) => void;
  setSelectedMonth: (month: number) => void;
  setSelectedDay: (day: number) => void;
  setSelectedRangeStart: (date: number) => void;
  setSelectedRangeEnd: (date: number) => void;
  setSelectedRange: (startDate: number, endDate: number) => void;
  setSelectedDate: (date: string) => void;
  setShowCalendar: (show: boolean) => void;
  setCalendarLayout: (layout: CalendarLayout) => void;
  resetSelectedDate: () => void;
  resetSelectedRange: () => void;
}

const currentYear = moment().year();
const currentMonth = moment().month();

const defaultStore = {
  selectedYearsRange: {
    startYear: currentYear - 4,
    endYear: currentYear + 7,
  },
  selectedYear: currentYear,
  selectedMonth: currentMonth,
  selectedDay: null,
  selectedRangeStart: null,
  selectedRangeEnd: null,
  showCalendar: false,
  calendarLayout: "days" as CalendarLayout,
  incrementSelectedYearsRange: () => {},
  decrementSelectedYearsRange: () => {},
  incrementSelectedYear: () => {},
  decrementSelectedYear: () => {},
  incrementSelectedMonth: () => {},
  decrementSelectedMonth: () => {},
  setSelectedYear: () => {},
  setSelectedMonth: () => {},
  setSelectedDay: () => {},
  setSelectedRangeStart: () => {},
  setSelectedRangeEnd: () => {},
  setSelectedRange: () => {},
  setSelectedDate: () => {},
  setShowCalendar: () => {},
  setCalendarLayout: () => {},
  resetSelectedDate: () => {},
  resetSelectedRange: () => {},
};

export const DatePickerContext =
  createContext<DatePickerStoreState>(defaultStore);

export const DatePickerProvider: FC<{ props?: ReactNode }> = ({ children }) => {
  const [store, setStore] = useState<DatePickerState>(defaultStore);

  const {
    selectedYearsRange,
    selectedYear,
    selectedMonth,
    selectedDay,
    selectedRangeStart,
    selectedRangeEnd,
    showCalendar,
    calendarLayout,
  } = store;

  const incrementSelectedYearsRange = () => {
    setStore({
      ...store,
      selectedYearsRange: {
        startYear: selectedYearsRange.endYear,
        endYear: selectedYearsRange.endYear + 11,
      },
    });
  };

  const decrementSelectedYearsRange = () => {
    setStore({
      ...store,
      selectedYearsRange: {
        startYear: selectedYearsRange.startYear - 11,
        endYear: selectedYearsRange.startYear,
      },
    });
  };

  const incrementSelectedYear = () => {
    setStore({
      ...store,
      selectedYear: selectedYear + 1,
    });
  };

  const decrementSelectedYear = () => {
    setStore({
      ...store,
      selectedYear: selectedYear - 1,
    });
  };

  const incrementSelectedMonth = () => {
    const month = selectedMonth + 1;
    const isNewYear = month > 11;
    setStore({
      ...store,
      selectedMonth: isNewYear ? 0 : month,
      selectedYear: isNewYear ? selectedYear + 1 : selectedYear,
    });
  };

  const decrementSelectedMonth = () => {
    const month = selectedMonth - 1;
    const isNewYear = month < 0;
    setStore({
      ...store,
      selectedMonth: isNewYear ? 11 : month,
      selectedYear: isNewYear ? selectedYear - 1 : selectedYear,
    });
  };

  const setSelectedYear = (year: number) => {
    setStore({ ...store, selectedYear: year, calendarLayout: "months" });
  };

  const setSelectedMonth = (month: number) => {
    setStore({ ...store, selectedMonth: month, calendarLayout: "days" });
  };

  const setSelectedDay = (day: number) => {
    setStore({ ...store, selectedDay: day, showCalendar: false });
  };

  const setSelectedDate = (date: string) => {
    const selectedDay = moment(date).valueOf();
    const selectedMonth = moment(date).month();
    const selectedYear = moment(date).year();
    setStore({
      ...store,
      selectedDay,
      selectedMonth,
      selectedYear,
    });
  };

  const setSelectedRangeStart = (date: number) => {
    setStore({
      ...store,
      selectedRangeStart: date,
    });
  };

  const setSelectedRangeEnd = (date: number) => {
    setStore({
      ...store,
      selectedRangeEnd: date,
    });
  };

  const setSelectedRange = (startDate: number, endDate: number) => {
    setStore({
      ...store,
      selectedRangeStart: startDate,
      selectedRangeEnd: endDate,
    });
  };

  const resetSelectedDate = () => {
    setStore({
      ...store,
      selectedDay: null,
      selectedMonth: currentMonth,
      selectedYear: currentYear,
    });
  };

  const resetSelectedRange = () => {
    setStore({
      ...store,
      selectedRangeStart: null,
      selectedRangeEnd: null,
    });
  };

  const setShowCalendar = (show: boolean) => {
    setStore({ ...store, showCalendar: show });
  };

  const setCalendarLayout = (layout: CalendarLayout) => {
    setStore({ ...store, calendarLayout: layout });
  };

  return (
    <DatePickerContext.Provider
      value={{
        selectedYearsRange,
        selectedYear,
        selectedMonth,
        selectedDay,
        selectedRangeStart,
        selectedRangeEnd,
        showCalendar,
        calendarLayout,
        incrementSelectedYearsRange,
        decrementSelectedYearsRange,
        incrementSelectedYear,
        decrementSelectedYear,
        incrementSelectedMonth,
        decrementSelectedMonth,
        setSelectedYear,
        setSelectedMonth,
        setSelectedDay,
        setSelectedDate,
        setSelectedRangeStart,
        setSelectedRangeEnd,
        setSelectedRange,
        setShowCalendar,
        setCalendarLayout,
        resetSelectedDate,
        resetSelectedRange,
      }}
    >
      {children}
    </DatePickerContext.Provider>
  );
};
